/**
 * WorkflowAgentConfigTabs — tab content panels for the agent config page.
 */

import type { WorkflowAgent, WorkflowSession } from "./types";
import styles from "./workflow.module.scss";

// ── Types ──────────────────────────────────────────────────────────────────

export type ConfigTab = "overview" | "config" | "sessions" | "logs";

const statusStyleMap: Record<string, string> = {
  active: "online",
  completed: "offline",
  archived: "offline",
  cancelled: "error",
};

// ── Mock data generators ───────────────────────────────────────────────────

const generateUptimeData = () => {
  const data = [];
  for (let i = 0; i < 24; i++) {
    data.push({
      hour: i,
      uptime: Math.random() > 0.1 ? 100 : Math.random() * 50,
    });
  }
  return data;
};

const generateRecentActivity = () => [
  { time: "10:45 AM", event: "Session started", details: "#cc-workflow", type: "info" },
  { time: "10:30 AM", event: "Tool executed", details: "code-review on PR #2305", type: "success" },
  { time: "10:15 AM", event: "Heartbeat received", details: "Status: Online", type: "info" },
  { time: "10:00 AM", event: "Configuration updated", details: "Instructions modified", type: "warning" },
  { time: "9:45 AM", event: "Session completed", details: "#engineering", type: "success" },
];

const generateLogs = () => [
  { timestamp: "2025-02-25 02:35:12", level: "INFO", message: "Session started for user @john in #cc-workflow" },
  { timestamp: "2025-02-25 02:34:58", level: "DEBUG", message: "Processing tool request: code-review" },
  { timestamp: "2025-02-25 02:34:45", level: "INFO", message: "Heartbeat sent to gateway" },
  { timestamp: "2025-02-25 02:34:30", level: "WARN", message: "Rate limit approaching" },
  { timestamp: "2025-02-25 02:34:15", level: "INFO", message: "Configuration reloaded successfully" },
  { timestamp: "2025-02-25 02:34:00", level: "ERROR", message: "Temporary connection timeout to Slack API (retrying)" },
  { timestamp: "2025-02-25 02:33:45", level: "INFO", message: "Tool execution completed: git-workflow" },
];

// ── Tab panels ─────────────────────────────────────────────────────────────

interface AgentConfigTabsProps {
  activeTab: ConfigTab;
  agent: WorkflowAgent;
  agentSessions: WorkflowSession[];
}

export const AgentConfigTabContent = ({ activeTab, agent, agentSessions }: AgentConfigTabsProps) => {
  switch (activeTab) {
    case "overview":
      return (
        <div className={styles.overviewTab}>
          <div className={styles.overviewGrid}>
            <div className={styles.overviewCard}>
              <h4>Uptime Chart (24h)</h4>
              <div className={styles.chartPlaceholder}>
                <div className={styles.uptimeChart}>
                  {generateUptimeData().map((point, i) => (
                    <div
                      key={i}
                      className={styles.uptimeBar}
                      style={{
                        height: `${point.uptime}%`,
                        background:
                          point.uptime > 90
                            ? "#10b981"
                            : point.uptime > 50
                              ? "#f59e0b"
                              : "#ef4444",
                      }}
                      title={`${point.hour}:00 - ${point.uptime.toFixed(1)}%`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.overviewCard}>
              <h4>Recent Activity</h4>
              <div className={styles.activityTimeline}>
                {generateRecentActivity().map((activity, i) => (
                  <div key={i} className={styles.activityItem}>
                    <div className={`${styles.activityDot} ${styles[activity.type]}`} />
                    <div className={styles.activityContent}>
                      <div className={styles.activityEvent}>{activity.event}</div>
                      <div className={styles.activityDetails}>{activity.details}</div>
                      <div className={styles.activityTime}>{activity.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );

    case "config":
      return (
        <div>
          <div className={styles.configSection}>
            <h3>Agent Configuration</h3>
            <div className={styles.configField}>
              <span className={styles.configLabel}>Slug</span>
              <span className={styles.configValue}>{agent.slug}</span>
            </div>
            <div className={styles.configField}>
              <span className={styles.configLabel}>Model</span>
              <span className={styles.configValue}>{agent.model ?? "(default)"}</span>
            </div>
            <div className={styles.configField}>
              <span className={styles.configLabel}>Enabled</span>
              <span className={styles.configValue}>{agent.enabled ? "Yes" : "No"}</span>
            </div>
            {agent.created_at && (
              <div className={styles.configField}>
                <span className={styles.configLabel}>Created</span>
                <span className={styles.configValue}>
                  {new Date(agent.created_at).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          {agent.description && (
            <div className={styles.configSection}>
              <h3>Description</h3>
              <div className={styles.promptBox}>{agent.description}</div>
            </div>
          )}

          <div className={styles.configSection}>
            <h3>Instructions</h3>
            <div className={styles.promptBox}>{agent.instructions || "(not set)"}</div>
          </div>

          <div className={styles.configSection}>
            <h3>Tools</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
              {agent.tools && agent.tools.length > 0 ? (
                agent.tools.map(tool => (
                  <span
                    key={tool}
                    className={styles.skillTag}
                    style={{ fontSize: "0.8rem", padding: "0.3rem 0.75rem" }}
                  >
                    {tool}
                  </span>
                ))
              ) : (
                <span style={{ color: "#888" }}>No tools configured</span>
              )}
            </div>
          </div>
        </div>
      );

    case "sessions":
      return (
        <div className={styles.sessionsTab}>
          <div className={styles.sessionsGrid}>
            {agentSessions.map(session => (
              <div key={session.id} className={styles.sessionSummaryCard}>
                <div className={styles.sessionCardHeader}>
                  <span
                    className={`${styles.statusBadge} ${styles[statusStyleMap[session.status] ?? "offline"]}`}
                  >
                    {session.status.replace(/_/g, " ")}
                  </span>
                  <span className={styles.sessionChannel}>{session.channel ?? "-"}</span>
                </div>
                <div className={styles.sessionStats}>
                  <div>{session.title ?? "(untitled)"}</div>
                  {session.created_at && (
                    <div>{new Date(session.created_at).toLocaleDateString()}</div>
                  )}
                </div>
              </div>
            ))}
            {agentSessions.length === 0 && (
              <div className={styles.noSessions}>No sessions found for this agent.</div>
            )}
          </div>
        </div>
      );

    case "logs":
      return (
        <div className={styles.logsTab}>
          <div className={styles.logsContainer}>
            {generateLogs().map((log, i) => (
              <div key={i} className={`${styles.logEntry} ${styles[log.level.toLowerCase()]}`}>
                <span className={styles.logTimestamp}>{log.timestamp}</span>
                <span className={styles.logLevel}>{log.level}</span>
                <span className={styles.logMessage}>{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      );

    default:
      return null;
  }
};
